import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { profileApi, ProfileModel } from '@pukka/api';
import { Button, Input, Label, Radio, ToggleGroup } from '@pukka/ui-components';
import { FaCheck } from 'react-icons/fa';

import rupiahImg from '../../assets/ic_amount.png';
import percentImg from '../../assets/ic_percentage.png';

import { DiscountCreateFormSchemaType } from './discountFormSchema';
import DiscountOutletModal from './DiscountOutletModal';
import discountState from './discountStore';

function DiscountForm() {
  const { data: profile } = profileApi.useProfile();
  const [outletOpen, setOutletOpen] = useState(false);

  const {
    register,
    formState: { errors, isDirty },
    watch,
    control,
    getValues,
  } = useFormContext<DiscountCreateFormSchemaType>();

  const category = watch('category');
  const outletIds = watch('outlet_ids') || [];

  const mappedOutlets = useMemo(() => {
    return (
      profile?.outlets?.reduce(
        (prev, acc) => {
          prev[acc.id || ''] = acc;

          return prev;
        },
        {} as Record<string, ProfileModel.Outlet>,
      ) || {}
    );
  }, [profile]);

  useEffect(() => {
    discountState.dirtyFormExists = isDirty;
  }, [isDirty]);

  return (
    <div className="px-4 py-2 text-sm">
      <div className="mb-5">
        <Label as="p" className="my-2.5">
          Discount Name
        </Label>
        <Input {...register('name')} type="text" placeholder="Name" className="w-full" />
        {errors.name && (
          <small className="text-red-500" data-testid="error-message">
            {errors.name.message}
          </small>
        )}
      </div>

      <div className="mb-5">
        <Label as="p" className="my-2.5">
          Input Configuration
        </Label>
        <Radio.Group orientation="vertical">
          <Radio id="fixedAmount" value="fixedAmount" {...register('category')} className="mb-2">
            <span className="flex flex-col text-sm">
              <span className="">Fixed amount</span>
              <span className="text-neutral-400">Amount configured in Back Office and cannot be changed in POS</span>
            </span>
          </Radio>

          <Radio id="customAmount" value="customAmount" {...register('category')}>
            <span className="flex flex-col text-sm">
              <span className="">Customizable amount</span>
              <span className="text-neutral-400">Amount to be decided in POS</span>
            </span>
          </Radio>
        </Radio.Group>
      </div>

      {category === 'fixedAmount' && (
        <div className="mb-5">
          <Label as="p" className="my-2.5">
            Discount Amount
          </Label>
          <div className="flex items-center gap-x-4">
            <Input type="number" {...register('amount')} placeholder="Amount" className="w-32" />
            <div className="flex items-center">
              <Controller
                control={control}
                name="amountType"
                render={({ field: { value, onChange } }) => (
                  <ToggleGroup value={value} onValueChange={onChange} type="single">
                    <ToggleGroup.Item value="cash">Rp</ToggleGroup.Item>
                    <ToggleGroup.Item value="percentage">%</ToggleGroup.Item>
                  </ToggleGroup>
                )}
              />
            </div>
          </div>
          {errors.amount && (
            <small className="block text-red-500" data-testid="error-message">
              {errors.amount.message}
            </small>
          )}
          {errors.amountType && (
            <small className="block text-red-500" data-testid="error-message">
              {errors.amountType.message}
            </small>
          )}
        </div>
      )}

      {category === 'customAmount' && (
        <div className="mb-5">
          <Label as="p" className="my-2.5">
            Select Promo Type
          </Label>
          <Radio.Group orientation="horizontal">
            <Radio.Card id="cash" className="flex-1" value="cash" {...register('discountTypeCustom')}>
              <span className="flex flex-col items-center justify-center pb-4">
                <img alt="default option" src={rupiahImg} className="mb-2" />
                <span className="font-medium">Deduction Amount (Rp)</span>
                <span className="text-neutral-500">e.g Rp. 10.000 of Total Sales</span>
              </span>
            </Radio.Card>
            <Radio.Card id="percentage" className="flex-1" value="percentage" {...register('discountTypeCustom')}>
              <span className="flex flex-col items-center justify-center pb-4">
                <img alt="default option" src={percentImg} className="mb-2" />
                <span className="font-medium">Deduction Amount (%)</span>
                <span className="text-neutral-500">e.g 15% of Total Sales</span>
              </span>
            </Radio.Card>
          </Radio.Group>
          {errors.discountTypeCustom && (
            <small className="text-red-500" data-testid="error-message">
              {errors.discountTypeCustom?.message}
            </small>
          )}
        </div>
      )}

      {profile?.outlets?.length && profile?.outlets?.length > 1 && (
        <div className="mb-3">
          <Label as="p" className="my-2.5">
            Where do you want to implement this discount?
          </Label>
          <Button className="mb-3 w-full" onClick={() => setOutletOpen(true)}>
            Assign Outlet
          </Button>
          <div className="mb-2 border-b border-neutral-200 pb-1 text-xs text-neutral-500">ASSIGNED OUTLETS</div>
          {errors.outlet_ids && (
            <small className="text-red-500" data-testid="error-message">
              {errors.outlet_ids?.message}
            </small>
          )}
          <ul className="flex flex-col gap-y-2 pl-2 text-neutral-700">
            {outletIds.map((outletId) => (
              <li className="flex items-center gap-x-2" key={outletId}>
                <FaCheck size={10} />
                {mappedOutlets[outletId]?.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DiscountOutletModal control={control} open={outletOpen} onOpenChange={setOutletOpen} defaultOutletIds={getValues('outlet_ids')} />
    </div>
  );
}

export default DiscountForm;
